import React, {
	Fragment,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import './table.css';

import {
	ColumnDef,
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	getFacetedRowModel,
	getFacetedUniqueValues,
	getFilteredRowModel,
	getGroupedRowModel,
	getSortedRowModel,
	useReactTable,
	sortingFns,
} from '@tanstack/react-table';
import { Listbox, Menu, Transition } from '@headlessui/react';
import {
	AdjustmentsHorizontalIcon,
	AdjustmentsVerticalIcon,
	CheckIcon,
	ChevronDownIcon,
	ChevronRightIcon,
	ChevronUpDownIcon,
	ChevronUpIcon,
	FunnelIcon,
	MagnifyingGlassIcon,
	PlusIcon,
	RocketLaunchIcon,
	ServerStackIcon,
	Squares2X2Icon,
	XCircleIcon,
} from '@heroicons/react/24/outline';
import { classNames, objectAccess } from '../helpers/functions';
import useModal from '../../hooks/useModal';
import SimpleDNDList from '../SimpleDNDList';
import {
	TemplateBoolean,
	TemplateDropdown,
	TemplateField,
	TemplateReactSelect,
} from '../Attributes/Attributes_old';
import Modal from '../Shell/Modal';
import SettingsContext from '../../context/SettingsContext';
import useSettings from '../../hooks/useSettings';
import { getModuleFields } from '../../api/modules';
import useAsync from '../../hooks/useAsync';
import usePrevious from '../../hooks/usePrevious';
import { useNavigate } from 'react-router-dom';
import {
	createTableView,
	deleteTableView,
	getTableView,
	updateTableView,
} from '../../api/table-views';
import { AssetSelectorBeta, MultiSelectPopover } from '../nextgen';
import Button from '../Button';
import { isEqual, setWith } from 'lodash';
import { sortData } from './tanstackUtils';
import Banner from '../Banner';

const BOOLEAN_OPTIONS = [
	{ label: 'Not set', value: 'not-set' },
	{ label: 'True', value: true },
	{ label: 'False', value: false },
];

const between = (x, min, max) => {
	return x ? x >= min && x <= max : false;
};

const bool = (x) => {
	return x || x === 'true';
};

const handleScroll = (event) => {
	const container = event.target;
	const scrollAmount = event.deltaY;
	container.scrollTo({
		top: 0,
		left: container.scrollLeft + scrollAmount,
		behavior: 'smooth',
	});
};

const DebouncedInput = ({
	value: initialValue,
	onChange,
	type,
	debounce = 500,
	original = false,
	...props
}) => {
	const [value, setValue] = useState(initialValue);

	useEffect(() => {
		setValue(initialValue);
	}, [initialValue]);

	useEffect(() => {
		const timeout = setTimeout(() => {
			onChange(value);
		}, debounce);

		return () => clearTimeout(timeout);
	}, [value]);

	return original ? (
		<input
			{...props}
			value={value}
			onChange={(e) => setValue(e.target.value)}
		/>
	) : (
		<TemplateField
			onChange={(index, value) => setValue(value)}
			value={value}
			onKeyDown={(e) => {
				if (e.code === 'Space') {
					e.stopPropagation();
				}
			}}
			{...props}
		/>
	);
};

const Filter = ({
	column,
	columnFilters,
	resetColumnFilterValue,
	originalRows,
	setHasChanges,
}) => {
	const columnId = column.id;
	const MANUFACTURER_FILTERS = columnFilters
		?.flatMap((o) => o.id === 'manufacturer' && o.value)
		.filter(Boolean);

	const uniqueRows = [
		...new Set(
			originalRows?.flatRows?.flatMap((row) => {
				const CUSTOM_ARRAY_DATA = ['tags'];
				const CUSTOM_DATA = ['vendor', 'category', 'location'];
				const CUSTOM_FIELD_PATHS = {
					vendor: 'vendor.companyName',
					category: 'category.name',
					location: 'location.name',
				};

				const column = row?.original?.custom_attributes?.find(
					(attr) => attr?.attributeFieldId === columnId,
				);

				const val = row.getValue(columnId);

				if (MANUFACTURER_FILTERS?.length && columnId === 'model') {
					return MANUFACTURER_FILTERS?.includes(
						row?.original?.manufacturer?.name,
					)
						? val
						: false;
				}
				return CUSTOM_DATA?.includes(column?.attribute_field?.type)
					? objectAccess(
							CUSTOM_FIELD_PATHS[column?.attribute_field?.type],
							column,
						)
					: columnId === 'tags'
						? val.map((o) => o.name)
						: val;
			}),
		),
	];

	const { filterVariant } = column?.columnDef.meta ?? {};

	const columnFilterValue = column.getFilterValue();

	const UNIQUE_OPTIONS = [
		{ label: 'All', value: '' },
		{ label: 'Not set', value: 'not-set' },
		...uniqueRows?.filter(Boolean).map((value) => {
			return { label: value, value };
		}),
	];

	const IS_MULTIVALUE = Array.isArray(columnFilterValue);

	return filterVariant === 'range' ? (
		<div className="col-span-1 flex item-center">
			<div className="grid grid-cols-2 gap-x-2">
				<div className="col-span-1 flex items-center">
					<DebouncedInput
						type="number"
						pattern="^-?[0-9]\d*\.?\d*$"
						min={Number(column.getFacetedMinMaxValues()?.[0] ?? '')}
						max={Number(column.getFacetedMinMaxValues()?.[1] ?? '')}
						value={columnFilterValue?.[0] ?? ''}
						onChange={(value) =>
							column.setFilterValue((old) => [value, old?.[1]])
						}
						placeholder={`Min ${
							column.getFacetedMinMaxValues()?.[0] !== undefined
								? `(${column.getFacetedMinMaxValues()?.[0]})`
								: ''
						}`}
					/>
				</div>
				<div className="col-span-1 flex items-center">
					<DebouncedInput
						type="number"
						pattern="^-?[0-9]\d*\.?\d*$"
						min={Number(column.getFacetedMinMaxValues()?.[0] ?? '')}
						max={Number(column.getFacetedMinMaxValues()?.[1] ?? '')}
						value={columnFilterValue?.[1] ?? ''}
						onChange={(value) =>
							column.setFilterValue((old) => [old?.[0], value])
						}
						placeholder={`Max ${
							column.getFacetedMinMaxValues()?.[1]
								? `(${column.getFacetedMinMaxValues()?.[1]})`
								: ''
						}`}
					/>
				</div>
			</div>
			<div
				className="flex items-center justify-center px-4 py-2 cursor-pointer"
				onClick={() => {
					let updatedColumnFilters = [...columnFilters];

					const columnFilterIndex = updatedColumnFilters?.findIndex(
						(o) => o.id === columnId,
					);

					updatedColumnFilters.splice(columnFilterIndex, 1);

					resetColumnFilterValue(updatedColumnFilters);
				}}
			>
				<XCircleIcon className="size-6 text-gray-600" />
			</div>
		</div>
	) : filterVariant === 'multiselect' ? (
		<div className="grid grid-cols-1 gap-y-2">
			{IS_MULTIVALUE ? (
				columnFilterValue?.map((filterValue, filterIndex) => {
					const value = UNIQUE_OPTIONS.find((o) => o.value === filterValue);
					return (
						<div className="col-span-1 flex item-center">
							<TemplateReactSelect
								className="flex-1"
								value={value}
								options={UNIQUE_OPTIONS}
								onChange={(index, selectedValue, raw) => {
									let updatedValue = columnFilterValue;
									updatedValue[filterIndex] = selectedValue?.value;
									column.setFilterValue(updatedValue);
									setHasChanges(true);
								}}
								disabled={!value}
								placeholder={!value ? 'Selected value deleted' : 'Select...'}
							/>
							<div
								className="flex items-center justify-center px-4 py-2 cursor-pointer"
								onClick={() => {
									let updatedValue = [...columnFilterValue];
									updatedValue.splice(filterIndex, 1);

									if (updatedValue?.length === 0) {
										let updatedColumnFilters = [...columnFilters];

										const columnFilterIndex = updatedColumnFilters?.findIndex(
											(o) => o.id === columnId,
										);

										updatedColumnFilters.splice(columnFilterIndex, 1);

										resetColumnFilterValue(updatedColumnFilters);
									} else {
										column.setFilterValue(updatedValue);
									}
									setHasChanges(true);
								}}
							>
								<XCircleIcon className="size-6 text-gray-600" />
							</div>
						</div>
					);
				})
			) : (
				<TemplateReactSelect
					options={UNIQUE_OPTIONS}
					onChange={(index, selectedValue, raw) => {
						//column.setFilterValue(selectedValue?.value);
					}}
				/>
			)}
		</div>
	) : filterVariant === 'boolean' ? (
		<div className="grid grid-cols-1 gap-y-2">
			{IS_MULTIVALUE &&
				columnFilterValue?.map((filterValue, filterIndex) => {
					return (
						<div className="col-span-1 flex item-center">
							<TemplateReactSelect
								className="flex-1"
								value={BOOLEAN_OPTIONS.find((o) => o.value === filterValue)}
								options={BOOLEAN_OPTIONS}
								onChange={(index, selectedValue, raw) => {
									let updatedValue = columnFilterValue;
									updatedValue[filterIndex] = selectedValue?.value;
									column.setFilterValue(updatedValue);
								}}
							/>
							<div
								className="flex items-center justify-center px-4 py-2 cursor-pointer"
								onClick={() => {
									let updatedValue = [...columnFilterValue];
									updatedValue.splice(filterIndex, 1);

									if (updatedValue?.length === 0) {
										let updatedColumnFilters = [...columnFilters];

										const columnFilterIndex = updatedColumnFilters?.findIndex(
											(o) => o.id === columnId,
										);

										updatedColumnFilters.splice(columnFilterIndex, 1);

										resetColumnFilterValue(updatedColumnFilters);
									} else {
										column.setFilterValue(updatedValue);
									}
								}}
							>
								<XCircleIcon className="size-6 text-gray-600" />
							</div>
						</div>
					);
				})}
		</div>
	) : (
		<div className="flex item-center">
			<div className="flex-1">
				<DebouncedInput
					type="text"
					value={columnFilterValue || ''}
					onChange={(value) => column.setFilterValue(value)}
					placeholder={`Search...`}
				/>
			</div>
			<div
				className="flex items-center justify-center px-4 py-2 cursor-pointer"
				onClick={() => {
					let updatedColumnFilters = [...columnFilters];

					const columnFilterIndex = updatedColumnFilters?.findIndex(
						(o) => o.id === columnId,
					);

					updatedColumnFilters.splice(columnFilterIndex, 1);

					resetColumnFilterValue(updatedColumnFilters);
				}}
			>
				<XCircleIcon className="size-6 text-gray-600" />
			</div>
		</div>
	);
};

const getCommonPinningStyles = (column) => {
	const isPinned = column.getIsPinned();
	const isLastLeftPinnedColumn =
		isPinned === 'left' && column.getIsLastColumn('left');
	const isFirstRightPinnedColumn =
		isPinned === 'right' && column.getIsFirstColumn('right');

	return {
		boxShadow: isLastLeftPinnedColumn
			? '#f9f9f9 -8px 0px 4px -4px inset'
			: isFirstRightPinnedColumn
				? '4px 0 4px -8px gray #f9f9f9'
				: undefined,
		left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
		right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
		opacity: isPinned ? 0.95 : 1,
		position: isPinned ? 'sticky' : 'relative',
		width: column.getSize(),
		zIndex: isPinned ? 1 : 0,
		fontWeight: isPinned ? 'font-semibold' : 'font-normal',
	};
};

const IndeterminateCheckbox = ({ indeterminate, className = '', ...rest }) => {
	const ref = React.useRef();

	React.useEffect(() => {
		if (typeof indeterminate === 'boolean') {
			ref.current.indeterminate = !rest.checked && indeterminate;
		}
	}, [ref, indeterminate]);

	return (
		<div onClick={(e) => e.stopPropagation()}>
			<input
				type="checkbox"
				ref={ref}
				className={className + ' cursor-pointer'}
				{...rest}
			/>
		</div>
	);
};

const NewDataTableBeta = ({
	data = [],
	columns,
	settingsMenuItems,
	onRow,
	tableSettings,
	moduleId,
	namePrefix,
	includeExcludeAssets, // assets only
	onRefreshTableViews,
	hideColumnsByDefault = true,
	savedViewId,
	defaultFilters,
}) => {
	const navigate = useNavigate();
	const tableContainer = useRef();

	const { get: getSetting, set: setSetting } = useSettings();

	const IS_DEFAULT_VIEW = savedViewId;
	const TABLE_ID = tableSettings?.id;

	const getDefaultColumnPinning = (value) => ({
		left: ['select', ...(value ? [value] : [])],
	});

	const [hasChanges, setHasChanges] = useState(false);

	const [containerWidth, setContainerWidth] = useState(500);

	const [columnOrder, setColumnOrder] = useState([]);
	const [globalFilter, setGlobalFilter] = useState('');
	const [columnVisibility, setColumnVisibility] = useState({
		id: false,
	});
	const [rowSelection, setRowSelection] = useState({});

	// Category-based include/exclude filters (React state for proper reactivity)
	const [includedLocations, setIncludedLocations] = useState(
		tableSettings?.includedLocations || []
	);
	const [excludedLocations, setExcludedLocations] = useState(
		tableSettings?.excludedLocations || []
	);
	const [includedCategories, setIncludedCategories] = useState(
		tableSettings?.includedCategories || []
	);
	const [excludedCategories, setExcludedCategories] = useState(
		tableSettings?.excludedCategories || []
	);

	// Sync category filters when tableSettings loads/changes (for saved views)
	// Always update to handle switching between views with/without category filters
	useEffect(() => {
		setIncludedLocations(tableSettings?.includedLocations || []);
		setExcludedLocations(tableSettings?.excludedLocations || []);
		setIncludedCategories(tableSettings?.includedCategories || []);
		setExcludedCategories(tableSettings?.excludedCategories || []);
	}, [tableSettings]);

	const [isManageColumnsModalOpen, setManageColumnsModal] = useState(false);
	const [createNewViewOpen, setCreateNewViewOpen] = useState(false);

	// Delete table view modal
	const { setClose: closeDeleteModal, setOpen: openDeleteModal } =
		useModal('deleteModal');

	const {
		value: moduleFields,
		execute: onRefreshModuleFields,
		status,
	} = useAsync(getModuleFields, {
		immediate: false,
	});

	const MAIN_COLUMN_OPTIONS = [
		{
			value: null,
			label: 'None',
		},
		...(moduleFields?.length
			? moduleFields.map((attr) => ({
					value: attr?.isDefault ? attr?.key : attr.id,
					label: attr.label,
				}))
			: []),
	];

	const { execute: onUpdateTableView, status: isUpdatingTableView } = useAsync(
		updateTableView,
		{ immediate: false },
	);

	const { execute: onCreateTableView, status: isCreatingTableView } = useAsync(
		createTableView,
		{ immediate: false },
	);

	const { execute: onDeleteTableView, status: isDeletingTableView } = useAsync(
		deleteTableView,
		{ immediate: false },
	);

	const prevOpen = usePrevious(isManageColumnsModalOpen);
	useEffect(() => {
		if (isManageColumnsModalOpen && !prevOpen) {
			onRefreshModuleFields({ id: moduleId });
		}
	}, [isManageColumnsModalOpen]);

	useEffect(() => {
		const handleResize = () => {
			if (tableContainer?.current) {
				setContainerWidth(tableContainer?.current?.offsetWidth);
			}
		};

		handleResize(); // Get initial width on mount

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const defaultColumns = useMemo(() => [
		{
			id: 'id',
			accessorKey: 'id',
			enableHiding: false,
			enableColumnFilter: false,
			meta: {
				filterVariant: 'includeExclude',
			},
			filterFn: 'includeExclude',
		},
		/*{
			id: 'select',
			header: ({ table }) => (
				<IndeterminateCheckbox
					{...{
						checked: table.getIsAllRowsSelected(),
						indeterminate: table.getIsSomeRowsSelected(),
						onChange: table.getToggleAllRowsSelectedHandler(),
					}}
				/>
			),
			cell: ({ row }) => (
				<div onClick={(e) => e.stopPropagation()}>
					<IndeterminateCheckbox
						{...{
							checked: row.getIsSelected(),
							disabled: !row.getCanSelect(),
							indeterminate: row.getIsSomeSelected(),
							onChange: row.getToggleSelectedHandler(),
						}}
					/>
				</div>
			),
			size: 55,
			enableResizing: false,
			enableHiding: false,
			hoverType: 'none',
		},*/
	]);

	const [allColumns] = useState(() => [...defaultColumns, ...columns]);
	const finalColumns = useMemo(() => allColumns, []);
	const finalRows = useMemo(() => data?.rows, []);

	const table = useReactTable({
		data: finalRows || [],
		columns: finalColumns,
		getCoreRowModel: getCoreRowModel(),
		debugTable: false,
		debugHeaders: false,
		debugColumns: false,
		columnResizeMode: 'onChange',
		initialState: {
			included: tableSettings?.included?.map((o) => o?.id || o), // sometimes, we retrieve the whole object, sometimes just the ID
			excluded: tableSettings?.excludedAssets
				? tableSettings?.excludedAssets?.map((o) => o?.id || o)
				: tableSettings?.excluded
					? tableSettings?.excluded?.map((o) => o?.id || o)
					: null, // sometimes, we retrieve the whole object, sometimes just the ID
			// Category-based include/exclude filters
			includedLocations: tableSettings?.includedLocations || [],
			excludedLocations: tableSettings?.excludedLocations || [],
			includedCategories: tableSettings?.includedCategories || [],
			excludedCategories: tableSettings?.excludedCategories || [],
			// Commented out for later implementation:
			// includedManufacturers: tableSettings?.includedManufacturers || [],
			// excludedManufacturers: tableSettings?.excludedManufacturers || [],
			// includedModels: tableSettings?.includedModels || [],
			// excludedModels: tableSettings?.excludedModels || [],
			filtersEnabled: tableSettings?.filtersEnabled,
			sorting: tableSettings?.sorting || [],
			grouping: tableSettings?.grouping || [],
			columnFilters: tableSettings?.columnFilters || defaultFilters || [],
			columnPinning: Object.keys(tableSettings?.columnPinning || {})?.length
				? tableSettings?.columnPinning
				: getDefaultColumnPinning(),
		},
		state: {
			rowSelection,
			globalFilter,
			columnVisibility,
			columnOrder,
		},
		enableRowSelection: true, //enable row selection for all rows
		onRowSelectionChange: setRowSelection,
		groupedColumnMode: false,
		getGroupedRowModel: getGroupedRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getSortedRowModel: getSortedRowModel(), //client-side sorting
		onGlobalFilterChange: setGlobalFilter,
		onColumnOrderChange: setColumnOrder,
		//onColumnFiltersChange: setColumnFilters,
		getFilteredRowModel: getFilteredRowModel(), //client side filtering
		getFacetedRowModel: getFacetedRowModel(), // client-side faceting
		getFacetedUniqueValues: getFacetedUniqueValues(), // generate unique values for select filter/autocomplete
		onColumnVisibilityChange: setColumnVisibility,
		//globalFilterFn: 'text', // built-in filter function
		//manualFiltering: false,
		filterFns: {
			// add a custom global filter function
			multiselect: (row, columnId, columnFilterValues) => {
				let filterValue;

				if (columnId === 'tags') {
					filterValue = row.getValue(columnId)
						? row.getValue(columnId)?.map((o) => o.name)
						: null;
				} else {
					filterValue = row.getValue(columnId)
						? row.getValue(columnId).toString().trim()
						: null;
				}

				let filtered = false;

				const SHOW_ALL =
					columnFilterValues?.filter((o) => o === '').length ===
					columnFilterValues?.length;
				if (SHOW_ALL) {
					return true;
				}

				const INCLUDED = table.getState().included;
				const EXCLUDED = table.getState().excluded;

				if (Array.isArray(EXCLUDED) && EXCLUDED?.length) {
					if (EXCLUDED.includes(row?.original?.id)) {
						return false;
					}
				}

				if (Array.isArray(INCLUDED) && INCLUDED?.length) {
					if (INCLUDED.includes(row?.original?.id)) {
						return true;
					}
				}

				for (const columnFilterValue of columnFilterValues) {
					if (columnFilterValue === '') {
						continue;
					} else if (columnFilterValue === 'not-set') {
						if (columnId === 'tags') {
							if (!filterValue?.length) {
								filtered = true;
								continue;
							}
						} else {
							if (
								filterValue === null ||
								filterValue === 'undefined' ||
								filterValue === undefined ||
								typeof filterValue === 'undefined'
							) {
								filtered = true;
								continue;
							}
						}
					} else {
						if (columnId === 'tags') {
							if (filterValue?.includes(columnFilterValue)) {
								filtered = true;
								continue;
							}
						} else {
							if (filterValue === columnFilterValue) {
								filtered = true;
								continue;
							}
						}
					}
				}

				return filtered;
			},
			customField: (row, columnId, columnFilterValues) => {
				return true;
			},
			range: (row, columnId, columnFilterValues) => {
				const INCLUDED = table.getState().included;
				const EXCLUDED = table.getState().excluded;

				if (Array.isArray(EXCLUDED) && EXCLUDED?.length) {
					if (EXCLUDED.includes(row?.original?.id)) {
						return false;
					}
				}
				if (Array.isArray(INCLUDED) && INCLUDED?.length) {
					if (INCLUDED.includes(row?.original?.id)) {
						return true;
					}
				}

				const filterValue = row.getValue(columnId)
					? row.getValue(columnId).toString().trim()
					: null;

				const value = filterValue ? parseInt(filterValue) : null;
				const [min, max] = columnFilterValues;

				if (min && max) return between(value, min, max);
				if (max) return between(value, 0, max);
				if (min) return value > min;
			},

			boolean: (row, columnId, columnFilterValues) => {
				const filterValue = row.getValue(columnId)
					? row.getValue(columnId).toString().trim()
					: null;

				let filtered = false;

				const SHOW_ALL =
					columnFilterValues?.filter((o) => o === '').length ===
					columnFilterValues?.length;
				if (SHOW_ALL) {
					return true;
				}

				const INCLUDED = table.getState().included;
				const EXCLUDED = table.getState().excluded;

				if (Array.isArray(EXCLUDED) && EXCLUDED?.length) {
					if (EXCLUDED.includes(row?.original?.id)) {
						return false;
					}
				}
				if (Array.isArray(INCLUDED) && INCLUDED?.length) {
					if (INCLUDED.includes(row?.original?.id)) {
						return true;
					}
				}

				for (const columnFilterValue of columnFilterValues) {
					if (columnFilterValue === '') {
						continue;
					} else if (columnFilterValue === 'not-set') {
						if (
							filterValue === null ||
							filterValue === 'undefined' ||
							filterValue === undefined ||
							typeof filterValue === 'undefined'
						) {
							filtered = true;
							continue;
						}
					} else {
						if (bool(filterValue)) {
							filtered = true;
							continue;
						}
					}
				}
				return filtered;
			},

			text: (row, columnId, columnFilterValues) => {
				const INCLUDED = table.getState().included;
				const EXCLUDED = table.getState().excluded;

				if (Array.isArray(EXCLUDED) && EXCLUDED?.length) {
					if (EXCLUDED.includes(row?.original?.id)) {
						return false;
					}
				}
				if (Array.isArray(INCLUDED) && INCLUDED?.length) {
					if (INCLUDED.includes(row?.original?.id)) {
						return true;
					}
				}

				const filterValue = row.getValue(columnId)
					? row.getValue(columnId).toString().trim()
					: null;

				const IS_INPUT_EMPTY = Array.isArray(columnFilterValues)
					? columnFilterValues[0] === ''
					: columnFilterValues === '' || !columnFilterValues;

				return !IS_INPUT_EMPTY
					? filterValue
						? filterValue?.includes(columnFilterValues)
						: false
					: true;
			},
		},
	});

	useEffect(() => {
		let defaultHiddenColumns = {};

		if (hideColumnsByDefault) {
			defaultHiddenColumns = table
				?.getAllLeafColumns()
				.filter((column) => column.getCanHide())
				.filter((column) => column?.columnDef?.hiddenByDefault)
				?.reduce((obj, item) => Object.assign(obj, { [item.id]: false }), {});
		}

		const tableViewColumnsHidden = Object.keys(
			tableSettings?.columnVisibility || {},
		)?.length
			? tableSettings?.columnVisibility
			: {};

		setColumnVisibility({
			...columnVisibility,
			...defaultHiddenColumns,
			...tableViewColumnsHidden,
		});

		if (tableSettings?.columnOrder?.length) {
			setColumnOrder(tableSettings?.columnOrder);
		}
	}, []);

	const stateToObject = (state, table, categoryFilters = {}) => {
		const hiddenByDefault = table
			?.getAllLeafColumns()
			.filter((column) => column.getCanHide())
			.filter((column) => column?.columnDef?.hiddenByDefault)
			.map((o) => o.id);

		const updatedVisibility = Object.keys(columnVisibility || {})
			.filter((key) => key !== 'id')
			.filter((key) =>
				!columnVisibility[key] ? !hiddenByDefault.includes(key) : true,
			)
			.reduce(
				(obj, key) => Object.assign(obj, { [key]: columnVisibility[key] }),
				{},
			);

		return {
			...(state?.columnSizing ? { columnSizing: state?.columnSizing } : {}),
			...(typeof state?.filtersEnabled !== 'undefined'
				? { filtersEnabled: state?.filtersEnabled }
				: {}),
			...(state?.grouping ? { grouping: state?.grouping } : {}),
			...(state?.sorting ? { sorting: state?.sorting } : {}),
			...(state?.columnFilters ? { columnFilters: state?.columnFilters } : {}),
			...(state?.included ? { included: state?.included } : {}),
			...(state?.excluded ? { excluded: state?.excluded } : {}),
			...(state?.column ? { excluded: state?.excluded } : {}),
			...(state?.columnOrder ? { columnOrder: state?.columnOrder } : {}),
			...(state?.columnPinning ? { columnPinning: state?.columnPinning } : {}),
			...{ columnVisibility: updatedVisibility },
			// Category-based filters (from React state)
			...(categoryFilters.includedLocations?.length ? { includedLocations: categoryFilters.includedLocations } : {}),
			...(categoryFilters.excludedLocations?.length ? { excludedLocations: categoryFilters.excludedLocations } : {}),
			...(categoryFilters.includedCategories?.length ? { includedCategories: categoryFilters.includedCategories } : {}),
			...(categoryFilters.excludedCategories?.length ? { excludedCategories: categoryFilters.excludedCategories } : {}),
		};
	};

	const handleSaveView = () => {
		const {
			columnSizing,
			columnOrder,
			filtersEnabled,
			grouping,
			sorting,
			columnFilters,
			included,
			excluded,
			columnVisibility,
			columnPinning,
		} = table.getState();

		const hiddenByDefault = table
			?.getAllLeafColumns()
			.filter((column) => column.getCanHide())
			.filter((column) => column?.columnDef?.hiddenByDefault)
			.map((o) => o.id);
		const savedVisibility = Object.keys(tableSettings?.columnVisibility || {})
			.filter((key) => tableSettings?.columnVisibility[key])
			.reduce((obj, key) => Object.assign(obj, { [key]: true }), {});

		const updatedVisibility = Object.keys(columnVisibility || {})
			.filter((key) => key !== 'id')
			.filter((key) =>
				!columnVisibility[key] ? !hiddenByDefault.includes(key) : true,
			)
			.reduce(
				(obj, key) => Object.assign(obj, { [key]: columnVisibility[key] }),
				{},
			);

		const toSaveView = {
			...(isEqual(columnPinning, tableSettings?.columnPinning)
				? {}
				: { columnPinning }),
			...(isEqual(columnSizing, tableSettings?.columnSizing)
				? {}
				: { columnSizing }),
			...(filtersEnabled === tableSettings?.filtersEnabled
				? {}
				: { filtersEnabled }),
			...(isEqual(grouping, tableSettings?.grouping || []) ? {} : { grouping }),
			...(isEqual(sorting, tableSettings?.sorting) ? {} : { sorting }),
			...(isEqual(columnFilters, tableSettings?.columnFilters || [])
				? {}
				: { columnFilters }),
			...(isEqual(included, tableSettings?.included || []) ? {} : { included }),
			...(isEqual(columnOrder, tableSettings?.columnOrder || [])
				? {}
				: { columnOrder }),
			...(isEqual(
				excluded,
				tableSettings?.excludedAssets?.map(
					(x) => x?.table_view_assets_excluded?.assetId,
				) || [],
			)
				? {}
				: { excluded }),
			...(isEqual(savedVisibility, updatedVisibility)
				? {}
				: { columnVisibility: updatedVisibility }),
			// Category-based filters (from React state)
			...(isEqual(includedLocations, tableSettings?.includedLocations || [])
				? {}
				: { includedLocations }),
			...(isEqual(excludedLocations, tableSettings?.excludedLocations || [])
				? {}
				: { excludedLocations }),
			...(isEqual(includedCategories, tableSettings?.includedCategories || [])
				? {}
				: { includedCategories }),
			...(isEqual(excludedCategories, tableSettings?.excludedCategories || [])
				? {}
				: { excludedCategories }),
		};

		if (TABLE_ID) {
			onUpdateTableView(
				{
					id: TABLE_ID,
					...toSaveView,
				},
				() => {
					setHasChanges(false);
				},
				{ silent: true },
			);
		} else if (IS_DEFAULT_VIEW) {
			setSetting(
				'user',
				IS_DEFAULT_VIEW,
				stateToObject(table?.getState(), table, {
					includedLocations,
					excludedLocations,
					includedCategories,
					excludedCategories,
				}),
				{
					showNotification: false,
				},
			);
			setHasChanges(false);
		}
	};

	const activeFilterCount = table.getState().columnFilters?.length;
	const IS_TABLE_GROUPED = table.getState().grouping?.length > 0;
	const TABLE_INCLUSIONS = table.getState().included?.length || 0;
	const TABLE_EXCLUSIONS = table.getState().excluded?.length || 0;

	// Category-based filter counts (using React state)
	const TABLE_CATEGORY_FILTERS =
		(includedLocations?.length || 0) +
		(excludedLocations?.length || 0) +
		(includedCategories?.length || 0) +
		(excludedCategories?.length || 0);

	const TABLE_IN_EX = TABLE_INCLUSIONS + TABLE_EXCLUSIONS + TABLE_CATEGORY_FILTERS;
	const GROUPABLE_COLUMNS = table
		.getAllColumns()
		.filter((col) => col.getIsVisible() && col.getCanGroup());
	const FILTERABLE_COLUMNS = table
		.getAllColumns()
		.filter((col) => col.getCanFilter());
	const HAS_CHANGES =
		hasChanges ||
		!isEqual(
			table.getState().columnSizing,
			tableSettings?.columnSizing || {},
		) ||
		!isEqual(table.getState().sorting, tableSettings?.sorting || []);

	const FILTERS_ENABLED = TABLE_ID ? table?.getState().filtersEnabled : true;

	// Process assets data for selector
	const processedAssets = useMemo(() => {
		if (!data?.rows) return [];
		return data.rows.map((asset) => ({
			id: asset.id,
			name:
				[
					asset.manufacturer?.name || asset.manufacturerName,
					asset.model?.name || asset.modelName,
				]
					.filter(Boolean)
					.join(' ') || 'Unnamed Asset',
			manufacturer:
				asset.manufacturer?.name || asset.manufacturerName || 'Unknown',
			model: asset.model?.name || asset.modelName || '',
			type: asset.model?.name || asset.modelName || 'Unknown Type',
			location: asset.location?.name || asset.locationName || 'No Location',
			serial: asset.serialNumber || 'N/A',
		}));
	}, [data?.rows]);

	// Extract unique locations for category filtering
	// Use API-returned uniqueLocations (all org locations) for consistent options
	const uniqueLocations = useMemo(() => {
		// Prefer API-returned locations (includes all org locations regardless of filters)
		if (data?.uniqueLocations?.length) {
			return data.uniqueLocations
				.filter((loc) => loc?.id)
				.map((loc) => ({
					value: loc.id,
					label: loc.name || 'Unknown Location',
				}))
				.sort((a, b) => a.label.localeCompare(b.label));
		}
		// Fallback: extract from rows if API data not available
		if (!data?.rows) return [];
		const locations = data.rows
			.filter((asset) => asset.location?.id)
			.map((asset) => ({
				value: asset.location.id,
				label: asset.location.name || 'Unknown Location',
			}));
		return [...new Map(locations.map((l) => [l.value, l])).values()].sort(
			(a, b) => a.label.localeCompare(b.label),
		);
	}, [data?.uniqueLocations, data?.rows]);

	// Extract unique categories for category filtering
	// Use API-returned uniqueCategories (all org categories) for consistent options
	const uniqueCategories = useMemo(() => {
		// Prefer API-returned categories (includes all org categories regardless of filters)
		if (data?.uniqueCategories?.length) {
			return data.uniqueCategories
				.filter((cat) => cat?.id)
				.map((cat) => ({
					value: cat.id,
					label: cat.name || 'Unknown Category',
				}))
				.sort((a, b) => a.label.localeCompare(b.label));
		}
		// Fallback: extract from rows if API data not available
		if (!data?.rows) return [];
		const categories = data.rows
			.filter((asset) => asset.category?.id)
			.map((asset) => ({
				value: asset.category.id,
				label: asset.category.name || 'Unknown Category',
			}));
		return [...new Map(categories.map((c) => [c.value, c])).values()].sort(
			(a, b) => a.label.localeCompare(b.label),
		);
	}, [data?.uniqueCategories, data?.rows]);

	// Helper function to filter rows based on include/exclude settings
	const getFilteredRows = () => {
		const rows = table.getRowModel().rows || [];
		const included = table.getState().included;
		const excluded = table.getState().excluded;

		return rows
			.filter((o) => {
				// Individual asset include logic
				if (!FILTERS_ENABLED && included?.length > 0) {
					return included?.includes(o?.original?.id);
				}
				// Category-based include logic
				if (includedLocations.length > 0 && !includedLocations.includes(o?.original?.location?.id)) {
					return false;
				}
				if (includedCategories.length > 0 && !includedCategories.includes(o?.original?.category?.id)) {
					return false;
				}
				return true;
			})
			.filter((o) => {
				// Individual asset exclude logic
				if (excluded?.includes(o?.original?.id)) {
					return false;
				}
				// Category-based exclude logic
				if (excludedLocations.includes(o?.original?.location?.id)) {
					return false;
				}
				if (excludedCategories.includes(o?.original?.category?.id)) {
					return false;
				}
				return true;
			});
	};

	// Get filtered rows (called during render, no caching)
	const filteredTableRows = getFilteredRows();

	// Commented out for later implementation:
	// const uniqueManufacturers = useMemo(() => {
	// 	if (!data?.rows) return [];
	// 	const manufacturers = data.rows
	// 		.filter((asset) => asset.manufacturer?.id)
	// 		.map((asset) => ({
	// 			value: asset.manufacturer.id,
	// 			label: asset.manufacturer.name || 'Unknown Manufacturer',
	// 		}));
	// 	return [...new Map(manufacturers.map((m) => [m.value, m])).values()].sort(
	// 		(a, b) => a.label.localeCompare(b.label),
	// 	);
	// }, [data?.rows]);

	// const uniqueModels = useMemo(() => {
	// 	if (!data?.rows) return [];
	// 	const models = data.rows
	// 		.filter((asset) => asset.model?.id)
	// 		.map((asset) => ({
	// 			value: asset.model.id,
	// 			label: asset.model.name || 'Unknown Model',
	// 		}));
	// 	return [...new Map(models.map((m) => [m.value, m])).values()].sort(
	// 		(a, b) => a.label.localeCompare(b.label),
	// 	);
	// }, [data?.rows]);

	return (
		<div className="w-full -pl-12 -mr-12">
			<div className="md:hidden space-y-3">
				{filteredTableRows.map((row, rIdx) => {
						return (
							<div
								key={row.id}
								className="bg-white shadow p-4 border-l-4 border-blue-500 cursor-pointer"
								onClick={() => navigate(`/assets/${row?.id}/overview`)}
							>
								<div className="space-y-1 text-sm">
									{row.getVisibleCells().map((cell, cIdx) => {
										const { column } = cell;

										return (
											<div key={cIdx} className="flex">
												<span className="text-gray-600 font-medium text-xs flex-1">
													{column?.columnDef?.header}:
												</span>
												<span className="text-gray-800 text-xs flex-1">
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext(),
													)}
												</span>
											</div>
										);
									})}
								</div>
							</div>
						);
					})}
			</div>

			<div className="md:block">
				<div className="flex justify-between items-center pt-5 pb-5">
					<div className="flex items-center">
						<div className="grid grid-cols-1 mr-3">
							<DebouncedInput
								value={globalFilter || ''}
								onChange={(value) => setGlobalFilter(value)}
								id="search"
								name="search"
								placeholder="Search table..."
								original={true}
								className="col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pl-10 pr-3 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:pl-9 sm:text-sm/6"
							/>
							<MagnifyingGlassIcon
								aria-hidden="true"
								className="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-400 sm:size-4"
							/>
						</div>
						<Menu as="div" className="relative inline-block text-left mr-3">
							<div>
								<Menu.Button
									className={classNames(
										'inline-flex w-full justify-center items-center gap-x-1.5 rounded-md bg-white px-3 py-2.5 text-xs text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
										activeFilterCount ? 'font-semibold' : '',
									)}
								>
									<FunnelIcon
										className={classNames(
											'w-4 h-4 text-gray-400',
											activeFilterCount ? 'text-gray-600' : '',
										)}
									/>
									<span className="hidden xl:block">
										{`Filters${
											activeFilterCount ? ` (${activeFilterCount})` : ''
										}`}
									</span>
								</Menu.Button>
							</div>

							<Transition
								as={Fragment}
								enter="transition ease-out duration-100"
								enterFrom="transform opacity-0 scale-95"
								enterTo="transform opacity-100 scale-100"
								leave="transition ease-in duration-75"
								leaveFrom="transform opacity-100 scale-100"
								leaveTo="transform opacity-0 scale-95"
							>
								<Menu.Items className="absolute left-0 z-10 mt-2 w-[500] origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
									<div className="py-1 w-full px-8 py-6">
										<div
											className={classNames(
												TABLE_ID ? 'justify-between' : '',
												'flex items-center ',
											)}
										>
											<div className="text-md mb-3">Filters</div>
											{TABLE_ID && (
												<div className="flex items-center">
													<div className="text-xs text-gray-700 mr-3">
														Filters enabled
													</div>
													<TemplateBoolean
														value={FILTERS_ENABLED}
														onChange={(index, filtersEnabled) => {
															setHasChanges(true);
															table.setState((state) => ({
																...state,
																filtersEnabled,
															}));
															if (!filtersEnabled) {
																table.setColumnFilters([]);
															}
														}}
													/>
												</div>
											)}
										</div>

										{FILTERS_ENABLED ? (
											<>
												<div className="py-4">
													<div className="grid grid-cols-12 gap-4 text-sm text-gray-600">
														{table.getState().columnFilters?.length ? (
															table
																.getState()
																.columnFilters?.map((filter, fIdx) => {
																	const column = FILTERABLE_COLUMNS.find(
																		(o) => {
																			return o.id === filter.id;
																		},
																	);

																	if (!column) {
																		return (
																			<div className="col-span-12">
																				<Banner
																					status="error"
																					size="xs"
																					className={'cursor-pointer'}
																					onClick={() => {
																						let updatedColumnFilters = [
																							...table.getState()
																								?.columnFilters,
																						];
																						table.setColumnFilters([
																							...updatedColumnFilters.slice(
																								0,
																								fIdx,
																							),
																							...updatedColumnFilters.slice(
																								fIdx + 1,
																							),
																						]);
																						setHasChanges(true);
																					}}
																				>
																					A filter or attribute has been
																					deleted. Click to confirm delete
																				</Banner>
																			</div>
																		);
																	}

																	return (
																		<>
																			<div className="col-span-5 mt-3">
																				<div>{column?.columnDef.header}</div>
																			</div>
																			<div className="col-span-7">
																				<Filter
																					column={column}
																					columnFilters={
																						table.getState()?.columnFilters
																					}
																					resetColumnFilterValue={(
																						columnFilters2,
																					) => {
																						table.setColumnFilters(
																							columnFilters2,
																						);
																					}}
																					originalRows={table.getPreFilteredRowModel()}
																					setHasChanges={setHasChanges}
																					filter={filter}
																				/>
																			</div>
																		</>
																	);
																})
														) : (
															<div className="col-span-12 text-center">
																No filters active
															</div>
														)}
													</div>
												</div>
												<div>
													<Menu
														as="div"
														className="relative inline-block text-left mr-3"
													>
														<div>
															<Menu.Button
																className={classNames(
																	'inline-flex w-full justify-center items-center gap-x-1.5 rounded-md bg-white px-3 text-xs text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
																	'py-2.5',
																)}
															>
																Add Filter
																<PlusIcon
																	className={classNames(
																		'w-5 h-5 text-gray-400',
																	)}
																/>
															</Menu.Button>
														</div>

														<Transition
															as={Fragment}
															enter="transition ease-out duration-100"
															enterFrom="transform opacity-0 scale-95"
															enterTo="transform opacity-100 scale-100"
															leave="transition ease-in duration-75"
															leaveFrom="transform opacity-100 scale-100"
															leaveTo="transform opacity-0 scale-95"
														>
															<Menu.Items
																anchor="top start"
																className="[--anchor-gap:4px] sm:[--anchor-gap:8px] absolute left-0 z-10 mt-2 w-96 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
															>
																{FILTERABLE_COLUMNS?.length
																	? FILTERABLE_COLUMNS?.map((item) => {
																			return (
																				<Menu.Item>
																					{({ active }) => (
																						<div
																							onClick={() => {
																								/*
																									Allows filters to be multiselect
																								]*/

																								let updatedColumnFilters = [
																									...table.getState()
																										?.columnFilters,
																								];

																								const FILTER_EXISTS_INDEX =
																									table
																										.getState()
																										?.columnFilters?.findIndex(
																											(o) => o?.id === item.id,
																										);

																								if (FILTER_EXISTS_INDEX >= 0) {
																									let updatedValue =
																										table.getState()
																											?.columnFilters[
																											FILTER_EXISTS_INDEX
																										]?.value;

																									updatedValue.push(undefined);
																									updatedColumnFilters[
																										FILTER_EXISTS_INDEX
																									]['value'] = updatedValue;
																								} else {
																									updatedColumnFilters.push({
																										id: item?.id,
																										value: [''],
																									});
																								}

																								table.setColumnFilters(
																									updatedColumnFilters,
																								);
																							}}
																							className={classNames(
																								'text-gray-700 block px-4 py-2 text-sm cursor-pointer',
																								active
																									? 'bg-gray-100 text-gray-900'
																									: 'text-gray-700',
																							)}
																						>
																							{item?.columnDef?.header}
																						</div>
																					)}
																				</Menu.Item>
																			);
																		})
																	: null}
															</Menu.Items>
														</Transition>
													</Menu>
												</div>
											</>
										) : null}
									</div>
								</Menu.Items>
							</Transition>
						</Menu>

						<Menu as="div" className="relative inline-block text-left mr-3">
							<div>
								<Menu.Button
									className={classNames(
										'inline-flex w-full justify-center items-center gap-x-1.5 rounded-md bg-white px-3 text-xs text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 whitespace-nowrap',
										IS_TABLE_GROUPED ? 'font-semibold py-2' : 'py-2.5',
									)}
									onClick={
										IS_TABLE_GROUPED
											? (e) => {
													e.preventDefault();
													table.setGrouping([]);
													setHasChanges(true);
												}
											: undefined
									}
								>
									<Squares2X2Icon
										className={classNames(
											'w-4 h-4 text-gray-400',
											IS_TABLE_GROUPED ? 'text-gray-600' : '',
										)}
									/>
									<div className="hidden xl:flex xl:items-center">
										<span>{`${IS_TABLE_GROUPED ? ` Grouped by (1)` : 'Group by'}`}</span>
										{IS_TABLE_GROUPED && (
											<XCircleIcon
												className={classNames(
													'w-5 h-5 text-gray-400 ml-1.5',
													IS_TABLE_GROUPED ? 'text-gray-600' : '',
												)}
											/>
										)}
									</div>
								</Menu.Button>
							</div>

							<Transition
								as={Fragment}
								enter="transition ease-out duration-100"
								enterFrom="transform opacity-0 scale-95"
								enterTo="transform opacity-100 scale-100"
								leave="transition ease-in duration-75"
								leaveFrom="transform opacity-100 scale-100"
								leaveTo="transform opacity-0 scale-95"
							>
								<Menu.Items className="absolute right-0 z-10 mt-2 w-96 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none whitespace-nowrap">
									{GROUPABLE_COLUMNS?.length
										? GROUPABLE_COLUMNS?.map((item) => (
												<Menu.Item>
													{({ active }) => (
														<div
															onClick={() => {
																table.setGrouping([item.id]);
																setHasChanges(true);
															}}
															className={classNames(
																'text-gray-700 block px-4 py-2 text-sm cursor-pointer',
																active
																	? 'bg-gray-100 text-gray-900'
																	: 'text-gray-700',
															)}
														>
															{item?.columnDef?.header}
														</div>
													)}
												</Menu.Item>
											))
										: null}
								</Menu.Items>
							</Transition>
						</Menu>

						{includeExcludeAssets && (
							<Menu as="div" className="relative inline-block text-left mr-3">
								<div>
									<Menu.Button
										className={classNames(
											'inline-flex w-full justify-center items-center gap-x-1.5 rounded-md bg-white px-3 text-xs text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 whitespace-nowrap',
											TABLE_IN_EX > 0 ? 'font-semibold py-2.5' : 'py-2.5',
										)}
									>
										<ServerStackIcon
											className={classNames(
												'w-4 h-4 text-gray-400',
												TABLE_IN_EX > 0 ? 'text-gray-600' : '',
											)}
										/>
										<span className="hidden xl:block">
											{`Include / Exclude ${
												TABLE_IN_EX > 0 ? ` (${TABLE_IN_EX})` : ''
											}`}
										</span>
									</Menu.Button>
								</div>

								<Transition
									as={Fragment}
									enter="transition ease-out duration-100"
									enterFrom="transform opacity-0 scale-95"
									enterTo="transform opacity-100 scale-100"
									leave="transition ease-in duration-75"
									leaveFrom="transform opacity-100 scale-100"
									leaveTo="transform opacity-0 scale-95"
								>
									<Menu.Items className="absolute left-0 z-10 mt-2 w-[500] origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
										<div className="py-1 w-full px-8 py-6">
											<div className="text-sm font-semibold">{`${
												TABLE_INCLUSIONS
													? `Included (${TABLE_INCLUSIONS})`
													: 'Include'
											}`}</div>

											<div className="py-4">
												<AssetSelectorBeta
													value={table.getState().included || []}
													onChange={(assetIds) => {
														setHasChanges(true);
														table.setState((state) => ({
															...state,
															included: assetIds,
														}));
													}}
													placeholder="Select assets to include..."
													multiple={true}
													assets={processedAssets}
													disablePortal={true}
												/>
											</div>

											<div className="text-sm font-semibold">{`${
												TABLE_EXCLUSIONS
													? `Excluded (${TABLE_EXCLUSIONS})`
													: 'Exclude'
											}`}</div>

											<div className="py-4">
												<AssetSelectorBeta
													value={table.getState().excluded || []}
													onChange={(assetIds) => {
														setHasChanges(true);
														table.setState((state) => ({
															...state,
															excluded: assetIds,
														}));
													}}
													placeholder="Select assets to exclude..."
													multiple={true}
													assets={processedAssets}
													disablePortal={true}
												/>
											</div>

											{/* Category-based Include/Exclude */}
											<div className="border-t border-gray-200 mt-4 pt-4">
												<div className="text-sm font-semibold mb-2">Include by Location</div>
												<div className="py-2">
													<MultiSelectPopover
														value={includedLocations}
														options={uniqueLocations}
														onChange={(ids) => {
															setHasChanges(true);
															setIncludedLocations(ids);
														}}
														placeholder="Select locations..."
														disablePortal={true}
													/>
												</div>

												<div className="text-sm font-semibold mb-2 mt-3">Exclude by Location</div>
												<div className="py-2">
													<MultiSelectPopover
														value={excludedLocations}
														options={uniqueLocations}
														onChange={(ids) => {
															setHasChanges(true);
															setExcludedLocations(ids);
														}}
														placeholder="Select locations..."
														disablePortal={true}
													/>
												</div>

												<div className="text-sm font-semibold mb-2 mt-3">Include by Category</div>
												<div className="py-2">
													<MultiSelectPopover
														value={includedCategories}
														options={uniqueCategories}
														onChange={(ids) => {
															setHasChanges(true);
															setIncludedCategories(ids);
														}}
														placeholder="Select categories..."
														disablePortal={true}
													/>
												</div>

												<div className="text-sm font-semibold mb-2 mt-3">Exclude by Category</div>
												<div className="py-2">
													<MultiSelectPopover
														value={excludedCategories}
														options={uniqueCategories}
														onChange={(ids) => {
															setHasChanges(true);
															setExcludedCategories(ids);
														}}
														placeholder="Select categories..."
														disablePortal={true}
													/>
												</div>
											</div>
										</div>
									</Menu.Items>
								</Transition>
							</Menu>
						)}
					</div>
					<div>
						<div className="inline-flex rounded-md shadow-sm mr-3">
							<button
								type="button"
								className={classNames(
									HAS_CHANGES
										? 'text-gray-500 ring-gray-300 hover:bg-gray-50 '
										: 'text-gray-300 ring-gray-200 ',
									'relative inline-flex items-center rounded-l-md bg-white px-3 py-2 text-xs ring-1 ring-inset focus:z-10',
								)}
								disabled={!HAS_CHANGES}
								onClick={() => handleSaveView()}
							>
								<RocketLaunchIcon
									className={classNames(
										'size-4 mr-2',
										HAS_CHANGES ? 'text-gray-400 ' : 'text-gray-300 ',
									)}
								/>{' '}
								<span className="hidden xl:block">Save view</span>
							</button>
							<Menu as="div" className="relative -ml-px block">
								<Menu.Button className="relative inline-flex items-center rounded-r-md bg-white px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10">
									<span className="sr-only">Open options</span>
									<ChevronDownIcon aria-hidden="true" className="size-5" />
								</Menu.Button>
								<Menu.Items
									transition
									className="absolute right-0 z-10 -mr-1 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
								>
									<div className="py-1">
										<Menu.Item>
											<div
												onClick={() => setCreateNewViewOpen({})}
												className="cursor-pointer block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
											>
												Save as new view
											</div>
										</Menu.Item>
									</div>
								</Menu.Items>
							</Menu>
						</div>

						<div className="relative inline-block text-left">
							<div
								onClick={() => {
									setManageColumnsModal(true);
								}}
								className="cursor-pointer inline-flex w-full justify-center items-center gap-x-1.5 rounded-md bg-white px-3 py-2.5 text-xs text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
							>
								<span className="sr-only">Open options</span>
								<AdjustmentsVerticalIcon className="w-4 h-4 text-gray-400" />{' '}
								<span className="hidden xl:block">Manage Columns</span>
							</div>
						</div>
					</div>
				</div>

				<div
					className="container-scipods-table"
					onWheel={handleScroll}
					ref={tableContainer}
				>
					<div className="item">
						<div className="table-container">
							{IS_TABLE_GROUPED ? (
								<div>
									{table.getGroupedRowModel().rows.map((groupedTable) => {
										const { groupingColumnId, groupingValue } = groupedTable;

										const GROUPED_ROWS = sortData(groupedTable.subRows, table)
											//.sort()
											?.filter((o) => {
												// Individual asset include logic
												if (!FILTERS_ENABLED && table.getState().included?.length > 0) {
													return table.getState().included?.includes(o?.original?.id);
												}
												// Category-based include logic (using React state)
												if (includedLocations.length > 0 && !includedLocations.includes(o?.original?.location?.id)) {
													return false;
												}
												if (includedCategories.length > 0 && !includedCategories.includes(o?.original?.category?.id)) {
													return false;
												}
												return true;
											})
											?.filter((o) => {
												// Individual asset exclude logic
												if (table.getState().excluded?.includes(o?.original?.id)) {
													return false;
												}
												// Category-based exclude logic (using React state)
												if (excludedLocations.includes(o?.original?.location?.id)) {
													return false;
												}
												if (excludedCategories.includes(o?.original?.category?.id)) {
													return false;
												}
												return true;
											});

										if (!GROUPED_ROWS?.length) {
											return null;
										}

										const CUSTOM_DATA = ['vendor', 'category', 'location'];
										const CUSTOM_FIELD_PATHS = {
											vendor: 'vendor.companyName',
											category: 'category.name',
											location: 'location.name',
										};

										const column =
											groupedTable?.original?.custom_attributes?.find(
												(attr) => attr?.attributeFieldId === groupingColumnId,
											);

										const groupName = CUSTOM_DATA?.includes(
											column?.attribute_field?.type,
										)
											? objectAccess(
													CUSTOM_FIELD_PATHS[column?.attribute_field?.type],
													column,
												)
											: groupingValue;

										return (
											<>
												<div className="text-md font-semibold text-gray-700">
													{groupingValue === 'undefined' ||
													groupingValue === '' ||
													!groupName
														? 'Not set'
														: groupName}
												</div>

												<table
													style={{
														width:
															containerWidth < table.getTotalSize()
																? table.getTotalSize()
																: '100%',
													}}
													className="new-beta my-6"
												>
													<thead>
														{table.getHeaderGroups().map((headerGroup) => (
															<tr key={headerGroup.id}>
																{headerGroup.headers.map((header) => {
																	const { column } = header;

																	return (
																		<th
																			key={header.id}
																			colSpan={header.colSpan}
																			//IMPORTANT: This is where the magic happens!
																			style={{
																				...getCommonPinningStyles(column),
																			}}
																			className="sticky top-0 z-9 bg-gray-50/75 py-3.5 pl-4 pr-8 text-left text-xs font-normal whitespace-nowrap text-gray-500 backdrop-blur backdrop-filter"
																		>
																			<div className="whitespace-nowrap">
																				{header.isPlaceholder ? null : (
																					<div
																						{...{
																							className:
																								header.column.getCanSort()
																									? 'cursor-pointer select-none flex items-center'
																									: '',
																							onClick:
																								header.column.getToggleSortingHandler(),
																						}}
																					>
																						{flexRender(
																							header.column.columnDef.header,
																							header.getContext(),
																						)}

																						{header.column.getCanSort() &&
																							({
																								asc: (
																									<ChevronUpIcon className="w-3 h-3 font-bold text-gray-500 ml-1.5" />
																								),
																								desc: (
																									<ChevronDownIcon className="w-3 h-3 font-bold text-gray-500 ml-1.5" />
																								),
																							}[
																								header.column.getIsSorted()
																							] || (
																								<ChevronUpDownIcon className="w-4 h-4 font-bold text-gray-500 ml-1.5" />
																							))}
																					</div>
																				)}
																			</div>
																			{column?.getCanResize() && (
																				<div
																					{...{
																						onDoubleClick: () =>
																							header.column.resetSize(),
																						onMouseDown:
																							header.getResizeHandler(),
																						onTouchStart:
																							header.getResizeHandler(),
																						className: `resizer ${
																							header.column.getIsResizing()
																								? 'isResizing'
																								: ''
																						}`,
																					}}
																				/>
																			)}
																		</th>
																	);
																})}
															</tr>
														))}
													</thead>
													<tbody>
														{GROUPED_ROWS.map((row, rIdx) => (
															<tr key={rIdx}>
																{row.getVisibleCells().map((cell, cIdx) => {
																	const { column } = cell;
																	const hoverType =
																		column?.columnDef?.hoverType;
																	const cellType =
																		typeof column?.columnDef?.cellType ===
																		'function'
																			? column?.columnDef?.cellType(cell)
																			: null;

																	const isLinkable =
																		typeof onRow === 'function';

																	return (
																		<td
																			key={cIdx}
																			//IMPORTANT: This is where the magic happens!
																			className={classNames(
																				'bg-white group py-2.5 pl-4 pr-8 whitespace-nowrap whitespace-nowrap whitespace-nowrap text-xs text-gray-400',
																				hoverType === 'chevron-link'
																					? 'hover:bg-gray-50 hover:outline-gray-200 cursor-pointer !pr-4'
																					: '',

																				isLinkable
																					? hoverType === 'none'
																						? ''
																						: 'cursor-pointer'
																					: '',
																				cellType,
																			)}
																			style={{
																				...getCommonPinningStyles(column),
																			}}
																			onClick={() =>
																				hoverType === 'none'
																					? {}
																					: isLinkable
																						? onRow(row)
																						: {}
																			}
																		>
																			{hoverType === 'chevron-link' ? (
																				<div className="flex justify-between">
																					{flexRender(
																						cell.column.columnDef.cell,
																						cell.getContext(),
																					)}
																					<ChevronRightIcon className="w-4 h-4 text-gray-500 opacity-0 group-hover:opacity-100" />
																				</div>
																			) : (
																				<div
																					className={classNames(
																						hoverType === 'selectable'
																							? 'inline cursor-text'
																							: '',
																					)}
																					onClick={(e) =>
																						hoverType === 'selectable'
																							? e.stopPropagation()
																							: {}
																					}
																				>
																					{flexRender(
																						cell.column.columnDef.cell,
																						cell.getContext(),
																					)}
																				</div>
																			)}
																		</td>
																	);
																})}
															</tr>
														))}
													</tbody>
												</table>
											</>
										);
									})}
								</div>
							) : (
								<table
									className="new-beta"
									style={{
										width:
											containerWidth < table.getTotalSize()
												? table.getTotalSize()
												: '100%',
									}}
								>
									<thead>
										{table.getHeaderGroups().map((headerGroup) => (
											<tr key={headerGroup.id}>
												{headerGroup.headers.map((header) => {
													const { column } = header;

													return (
														<th
															key={header.id}
															colSpan={header.colSpan}
															//IMPORTANT: This is where the magic happens!
															style={{ ...getCommonPinningStyles(column) }}
															className="sticky top-0 z-9 bg-gray-50/75 py-3.5 pl-4 pr-8 text-left text-xs font-normal whitespace-nowrap text-gray-500 backdrop-blur backdrop-filter"
														>
															<div className="whitespace-nowrap">
																{header.isPlaceholder ? null : (
																	<div
																		{...{
																			className: header.column.getCanSort()
																				? 'cursor-pointer select-none flex items-center'
																				: '',
																			onClick:
																				header.column.getToggleSortingHandler(),
																		}}
																	>
																		{flexRender(
																			header.column.columnDef.header,
																			header.getContext(),
																		)}

																		{header.column.getCanSort() &&
																			({
																				asc: (
																					<ChevronUpIcon className="w-3 h-3 font-bold text-gray-500 ml-1.5" />
																				),
																				desc: (
																					<ChevronDownIcon className="w-3 h-3 font-bold text-gray-500 ml-1.5" />
																				),
																			}[header.column.getIsSorted()] || (
																				<ChevronUpDownIcon className="w-4 h-4 font-bold text-gray-500 ml-1.5" />
																			))}
																	</div>
																)}
															</div>
															{column?.getCanResize() && (
																<div
																	{...{
																		onDoubleClick: () =>
																			header.column.resetSize(),
																		onMouseDown: header.getResizeHandler(),
																		onTouchStart: header.getResizeHandler(),
																		className: `resizer ${
																			header.column.getIsResizing()
																				? 'isResizing'
																				: ''
																		}`,
																	}}
																/>
															)}
														</th>
													);
												})}
											</tr>
										))}
									</thead>
									<tbody>
										{filteredTableRows.map((row, rIdx) => (
												<tr key={row.id || rIdx}>
													{row.getVisibleCells().map((cell, cIdx) => {
														const { column } = cell;
														const hoverType = column?.columnDef?.hoverType;
														const isEditable = column?.columnDef?.isEditable;

														const isLinkable = typeof onRow === 'function';

														const cellType =
															typeof column?.columnDef?.cellType === 'function'
																? column?.columnDef?.cellType(cell)
																: null;

														return (
															<td
																key={cIdx}
																//IMPORTANT: This is where the magic happens!
																className={classNames(
																	'group py-2.5 pl-4 pr-8 whitespace-nowrap border-b border-gray-50  whitespace-nowrap whitespace-nowrap text-xs text-gray-400',
																	hoverType === 'chevron-link' || isEditable
																		? 'hover:bg-gray-50 hover:outline-gray-200 cursor-pointer !pr-4'
																		: '',

																	isLinkable
																		? hoverType === 'none'
																			? ''
																			: 'cursor-pointer'
																		: '',
																	cellType,
																)}
																style={{
																	...getCommonPinningStyles(column),
																}}
																onClick={() =>
																	hoverType === 'none' || isEditable
																		? {}
																		: isLinkable
																			? onRow(row)
																			: {}
																}
															>
																{hoverType === 'chevron-link' ? (
																	<div className="flex justify-between">
																		{flexRender(
																			cell.column.columnDef.cell,
																			cell.getContext(),
																		)}
																		<ChevronRightIcon className="w-4 h-4 text-gray-500 opacity-0 group-hover:opacity-100" />
																	</div>
																) : cell.column.columnDef.editingComponent ? (
																	cell.column.columnDef.editingComponent()
																) : (
																	<div
																		className={classNames(
																			hoverType === 'selectable'
																				? 'inline cursor-text'
																				: '',
																		)}
																		onClick={(e) =>
																			hoverType === 'selectable'
																				? e.stopPropagation()
																				: {}
																		}
																	>
																		{flexRender(
																			cell.column.columnDef.cell,
																			cell.getContext(),
																		)}
																	</div>
																)}
															</td>
														);
													})}
												</tr>
											))}
									</tbody>

									{/*<tfoot>
										{table.getFooterGroups().map((footerGroup) => (
											<tr key={footerGroup.id}>
												{footerGroup.headers.map((footer) => {
													const { column } = footer;
													return (
														<th
															key={footer.id}
															colSpan={footer.colSpan}
															//IMPORTANT: This is where the magic happens!
															style={{ ...getCommonPinningStyles(column) }}
															className="sticky top-0 z-9 bg-gray-50/75 py-3.5 pl-4 pr-8 sm:pl-6 lg:pl-8 text-left text-xs font-normal whitespace-nowrap text-gray-500 backdrop-blur backdrop-filter"
														>
															{flexRender(
																footer.column.columnDef.footer,
																footer.getContext()
															)}
														</th>
													);
												})}
											</tr>
										))}
											</tfoot>*/}
								</table>
							)}
						</div>
					</div>
				</div>
			</div>
			{/*}
			<div className="inline-block border border-black shadow rounded">
				<div className="px-1 border-b border-black mb-4">
					<label>
						<input
							{...{
								type: 'checkbox',
								checked: table.getIsAllColumnsVisible(),
								onChange: table.getToggleAllColumnsVisibilityHandler(),
							}}
						/>{' '}
						Toggle All
					</label>
				</div>
				{table.getAllLeafColumns().map((column) => {
					return (
						<div key={column.id} className="px-1">
							<label>
								<input
									{...{
										type: 'checkbox',
										checked: column.getIsVisible(),
										onChange: column.getToggleVisibilityHandler(),
									}}
								/>{' '}
								{column.id}
							</label>
						</div>
					);
				})}
			</div>*/}

			<Modal
				title="Create a new view"
				open={createNewViewOpen}
				onClose={() => setCreateNewViewOpen(false)}
			>
				<TemplateField
					name="View Name"
					autoFocus
					value={createNewViewOpen?.name}
					onChange={(index, name) =>
						setCreateNewViewOpen({ ...createNewViewOpen, name })
					}
				/>

				<div className="flex justify-end mt-3">
					<Button
						disabled={!createNewViewOpen?.name}
						onClick={() => {
							onCreateTableView(
								{
									name: createNewViewOpen?.name,
									tableTypeName: namePrefix,
									...stateToObject(table.getState(), table, {
										includedLocations,
										excludedLocations,
										includedCategories,
										excludedCategories,
									}),
								},
								(response) => {
									setCreateNewViewOpen(false);
									onRefreshTableViews();
									navigate(`/assets/table/${response?.id}`);
								},
								{ silent: true },
							);
						}}
					>
						Create View
					</Button>
				</div>
			</Modal>

			<Modal
				title="Manage Columns"
				open={isManageColumnsModalOpen}
				onClose={() => setManageColumnsModal(false)}
			>
				<div className="grid grid-cols-12 gap-x-3 gap-y-4">
					<div className="col-span-4 flex items-center">
						<div className="min-w-0 flex items-center text-sm leading-6">
							<label className="select-none text-xs text-gray-900">
								Pinned Column
							</label>
						</div>
					</div>
					<div className="col-span-8">
						<TemplateDropdown
							className={'w-72'}
							options={MAIN_COLUMN_OPTIONS}
							value={
								table.getState()?.columnPinning['left']?.length >= 2
									? table.getState()?.columnPinning['left'][1]
									: null
							}
							onChange={(index, value, raw) => {
								table.setColumnPinning(getDefaultColumnPinning(value));
								setHasChanges(true);
							}}
						/>
					</div>

					<div className="col-span-12">
						<div className="min-w-0 text-sm leading-6">
							<label className="select-none text-xs text-gray-900">
								Column Order
							</label>
						</div>
					</div>
					<div className="col-span-12">
						{/*<TemplateBoolean
							name="Toggle all"
							value={table?.getIsAllColumnsVisible()}
							onChange={(index, value) => table?.toggleAllColumnsVisible(value)}
							className={'mb-4'}
						/>*/}
						<SimpleDNDList
							items={table
								?.getAllLeafColumns()
								.filter((column) => !['id', 'select'].includes(column?.id))
								.map((column) => {
									return {
										id: column?.id,
										label: column?.columnDef?.header,
										value: column?.id,
										disabled: !column?.getCanHide(),
										checked: column?.getIsVisible(),
										onChange: (value) => {
											column?.toggleVisibility(value);
											setHasChanges(true);
										},
									};
								})}
							Card={({ item }) => {
								return (
									<div className="rounded-lg mb-2 px-6 py-3 border border-gray-200 px-6 flex justify-between text-xs items-center">
										<div>{item?.label}</div>
										<div>
											<TemplateBoolean
												value={item?.checked}
												onChange={(index, value) => item?.onChange(value)}
												disabled={item?.disabled}
											/>
										</div>
									</div>
								);
							}}
							onChange={(updatedList) => {
								setColumnOrder(
									updatedList(
										table
											?.getAllLeafColumns()
											.filter(
												(column) => !['id', 'select'].includes(column?.id),
											)
											.map((column) => column?.id),
									),
								);
								setHasChanges(true);
							}}
						/>
					</div>
				</div>

				<div className="flex justify-end my-4">
					<Button
						onClick={() => {
							handleSaveView();
							setManageColumnsModal(false);
						}}
						disabled={!HAS_CHANGES}
					>
						Save Columns
					</Button>
				</div>
			</Modal>
		</div>
	);
};

export default NewDataTableBeta;
